import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { changeMemberRole, type Member, type MemberRole } from '../api/members-api'

const ROLE_OPTIONS: { value: MemberRole; label: string }[] = [
  { value: 'admin', label: 'مدیر' },
  { value: 'data_entry', label: 'ورود داده' },
  { value: 'approver', label: 'بررسی‌کننده' },
  { value: 'viewer', label: 'مشاهده‌گر' },
]

interface ChangeRoleModalProps {
  projectId: string
  member: Member | null
  open: boolean
  onClose: () => void
}

export function ChangeRoleModal({ projectId, member, open, onClose }: ChangeRoleModalProps) {
  const qc = useQueryClient()
  const [newRole, setNewRole] = useState<MemberRole>(member?.role ?? 'viewer')

  const mutation = useMutation({
    mutationFn: () => changeMemberRole(member!.id, newRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', projectId] })
      onClose()
    },
  })

  if (!member) return null

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>تغییر نقش عضو</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted rounded-lg px-4 py-3">
            <p className="text-sm font-medium text-foreground">{member.name}</p>
            <p className="text-xs text-muted-foreground">نقش فعلی: {ROLE_OPTIONS.find(r => r.value === member.role)?.label}</p>
          </div>

          <div className="space-y-2">
            <Label>نقش جدید</Label>
            <Select value={newRole} onValueChange={(v) => setNewRole(v as MemberRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>انصراف</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || newRole === member.role}
          >
            {mutation.isPending ? 'در حال تغییر...' : 'تغییر نقش'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
